import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  User, Phone, Mail, MapPin, Globe, MessageSquare, Loader2, CheckCircle2,
  ArrowRight, ShieldCheck, Sparkles, AlertCircle, Briefcase, PhoneCall, FileCheck2, ListChecks,
} from 'lucide-react';
import {
  INDIAN_STATES, LANGUAGES, NAV_CATEGORIES, SERVICE_CATALOG, isIncomeTaxService,
  getServiceGuide,
  defaultSpokenLanguageForUiLocale,
} from '../mock';
import { ServiceMarquee } from '../components/ServiceMarquee';
import { GuideText } from '../components/GuideText';
import ServicePrice from '../components/ServicePrice';
import { useLanguage } from '../i18n/LanguageContext';
import {
  serviceTitle, serviceDesc, categoryTimeline,
} from '../i18n/serviceCopy';
import { getJourneyId, trackEvent, trackPageLifecycle } from '../lib/customerEventTracker';

const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';
const API_TIMEOUT_MS = 15000;
const MOCK_DELAY_MS = 800;

const initialState = {
  full_name: '', email: '', mobile: '',
  state: '', language: '', remark: '', service_required: [],
};

const GST_REGISTRATION_ENTITY_GUIDE = [
  {
    entityType: 'PROPRIETARY',
    label: 'Proprietor GST Registration',
    price: 'INR 999',
    docs: [
      'PAN Card of proprietor',
      'Aadhaar card of proprietor',
      'Passport size photograph',
      'Property tax receipt for registered office',
      'Rental agreement (if premises is rented)',
      'NOC (if premises is owned by another person)',
      'Cancelled cheque / passbook / bank statement',
    ],
  },
  {
    entityType: 'PARTNERSHIP_FIRM',
    label: 'Partnership Firm GST Registration',
    price: 'INR 1499',
    docs: [
      'PAN card of all partners',
      'Aadhaar card of all partners',
      'Passport photos of partners',
      'Property tax receipt for office address',
      'Rental agreement (if office is rented)',
      'NOC (if premises owned by someone else)',
      'Cancelled cheque / passbook / bank statement',
      'Partnership deed and registration certificate',
      'Authorization letter for GST filing',
    ],
  },
  {
    entityType: 'COMPANY',
    label: 'Company GST Registration',
    price: 'INR 1999',
    docs: [
      'PAN card of all directors',
      'Aadhaar card of all directors',
      'Passport photos of directors',
      'Property tax receipt for office address',
      'Rental agreement (if office is rented)',
      'NOC (if premises owned by someone else)',
      'Cancelled cheque / passbook / bank statement',
      'Authorization letter for GST filing',
    ],
  },
];

const validateForm = (form, t) => {
  const e = {};
  if (!form.full_name.trim() || form.full_name.length > 150) e.full_name = t('form.err_name', 'Enter a valid name (up to 150 chars)');
  if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('form.err_email', 'Enter valid email');
  if (!/^[0-9]{10}$/.test(form.mobile)) e.mobile = t('form.err_mobile', 'Enter a valid 10-digit mobile number');
  if (form.service_required.length === 0) e.service_required = t('sf.err_services', 'Select at least one service');
  return e;
};

const parseApiError = (err) => {
  const response = err?.response;
  const detail = response?.data?.detail;
  const message = detail?.error?.message
    || (typeof detail === 'string' ? detail : null)
    || err?.message
    || 'Request failed';
  const fields = detail?.error?.fields && typeof detail.error.fields === 'object'
    ? detail.error.fields
    : {};
  return { status: response?.status, message, fields };
};

const FormSection = ({ title, icon: Icon, children, hint }) => (
  <div>
    <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
      <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#14A098]" /></div>
      <h2 className="font-bold text-[#0f2540] text-lg">{title}</h2>
      {hint && <span className="text-xs text-gray-500 ml-auto">{hint}</span>}
    </div>
    {children}
  </div>
);

const FieldWrap = ({ label, icon: Icon, required, error, hint, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#14A098]" />} {label}
      {required && <span className="text-rose-500">*</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && <p className="text-xs text-rose-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

/** Parses first INR amount from guide strings like "INR 499 onwards" or "INR 999 / month onwards". */
const parseInrStartingAmount = (priceStr) => {
  if (!priceStr || typeof priceStr !== 'string') return { amount: null, isMonthly: false };
  const isMonthly = /\/\s*month/i.test(priceStr) || /\/\s*నెల/i.test(priceStr);
  const m = priceStr.match(/INR\s*([\d,]+)/i);
  if (!m) return { amount: null, isMonthly };
  const n = parseInt(String(m[1]).replace(/,/g, ''), 10);
  return { amount: Number.isFinite(n) ? n : null, isMonthly };
};

const ServicePicker = ({ selected, onToggle, error, includeIncomeTax = false }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  // Default excludes income-tax services; embedded home form can include all.
  // NAV_CATEGORIES is a module-level constant — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cats = useMemo(
    () => (includeIncomeTax ? NAV_CATEGORIES : NAV_CATEGORIES.filter((c) => c.category !== 'INCOME_TAX')),
    [includeIncomeTax],
  );
  const selectedServices = useMemo(
    () => SERVICE_CATALOG.filter((svc) => selected.includes(svc.code)),
    [selected],
  );
  const filteredCats = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cats;
    return cats
      .map((cat) => ({
        ...cat,
        services: cat.services.filter((svc) => {
          const titleTr = serviceTitle(svc.code, t, svc.title);
          const descTr = serviceDesc(svc.code, t, svc.desc);
          return (
            titleTr.toLowerCase().includes(q)
            || descTr.toLowerCase().includes(q)
            || svc.title.toLowerCase().includes(q)
            || svc.desc.toLowerCase().includes(q)
            || svc.code.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.services.length > 0);
  }, [cats, query, t]);
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    if (!filteredCats.some((cat) => cat.category === activeCategory)) {
      setActiveCategory(filteredCats[0]?.category || '');
    }
  }, [filteredCats, activeCategory]);

  const activeServices = useMemo(
    () => filteredCats.find((cat) => cat.category === activeCategory)?.services || [],
    [filteredCats, activeCategory],
  );

  const selectionTotal = useMemo(() => {
    let sum = 0;
    let parsedCount = 0;
    let hasMonthly = false;
    selectedServices.forEach((svc) => {
      const raw = getServiceGuide(svc.code)?.startingPrice;
      const { amount, isMonthly } = parseInrStartingAmount(raw);
      if (amount != null) {
        sum += amount;
        parsedCount += 1;
      }
      if (isMonthly) hasMonthly = true;
    });
    return {
      sum,
      parsedCount,
      totalCount: selectedServices.length,
      hasMonthly,
      complete: parsedCount === selectedServices.length && selectedServices.length > 0,
    };
  }, [selectedServices]);

  return (
    <div>
      <Label className="text-sm font-medium text-[#0f2540] mb-3 block">
        {t('sf.services_need', 'Services You Need')} <span className="text-rose-500">*</span>
      </Label>

      <Input
        id="services-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('sf.search_ph', 'Search services by name')}
        className="mb-3"
      />

      {selectedServices.length > 0 && selectionTotal.sum > 0 && (
        <div className="mb-3 rounded-xl border border-[#14A098]/25 bg-teal-50/40 p-3">
          <p className="text-xs font-semibold text-[#0f2540]">
            {t('sf.total_selected_hint', 'Selected services summary is shown before submit')}
          </p>
        </div>
      )}

      {selectedServices.length > 0 && selectionTotal.sum === 0 && (
        <div className="mb-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3 text-sm text-gray-600">
          <span className="font-semibold text-[#0f2540]">{t('sf.total_label_short', 'Total amount:')}</span>{' '}
          {t('sf.total_unknown', 'starting prices not available for the current selection — our team will confirm on call.')}
        </div>
      )}

      <div className="space-y-4 pr-2 border border-gray-100 rounded-xl p-4 bg-gray-50/50">
        <div className="flex flex-wrap gap-2">
          {filteredCats.map((cat) => (
            <button
              key={cat.category}
              type="button"
              onClick={() => setActiveCategory(cat.category)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeCategory === cat.category
                  ? 'border-[#14A098] bg-[#14A098] text-white'
                  : 'border-gray-200 bg-white text-[#0f2540] hover:border-[#14A098]/40'
              }`}
            >
              {t(`category.${cat.category}`, cat.label)} ({cat.services.length})
            </button>
          ))}
        </div>

        {activeCategory && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#14A098] mb-2">
              {(() => {
                const ac = filteredCats.find((cat) => cat.category === activeCategory);
                return ac ? t(`category.${ac.category}`, ac.label) : '';
              })()}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeServices.map((svc) => {
                const checked = selected.includes(svc.code);
                return (
                  <label
                    key={svc.code}
                    className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      checked ? 'border-[#14A098] bg-teal-50' : 'border-gray-200 bg-white hover:border-[#14A098]/40'
                    }`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => onToggle(svc.code)} className="mt-0.5 data-[state=checked]:bg-[#14A098] data-[state=checked]:border-[#14A098]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-[#0f2540] flex items-start gap-1.5 leading-snug">
                        <svc.icon className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{serviceTitle(svc.code, t, svc.title)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</div>
                      <div className="mt-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#14A098]">{t('sf.starting_price', 'Starting price')}</span>
                        <div className="text-sm font-extrabold text-[#0f2540] mt-0.5">
                          <ServicePrice serviceCode={svc.code} className="" />
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {filteredCats.length === 0 && <p className="text-sm text-gray-500">{t('sf.no_match', 'No services match your search.')}</p>}
      </div>
      {error && <p className="text-xs text-rose-500 flex items-center gap-1 mt-2"><AlertCircle className="w-3 h-3" /> {error}</p>}
    </div>
  );
};

const Sidebar = ({ count, errors, submitError }) => {
  const { t } = useLanguage();
  const errorList = [
    ...(submitError ? [submitError] : []),
    ...Object.values(errors || {}).filter((value) => typeof value === 'string' && value.trim()),
  ];
  const uniqueErrors = [...new Set(errorList)];

  return (
  <div className="space-y-6">
    {uniqueErrors.length > 0 && (
      <Card className="p-5 rounded-2xl border border-rose-200 bg-rose-50">
        <h3 className="font-bold text-rose-700 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {t('sf.fix_errors_title', 'Please fix these errors')}
        </h3>
        <ul className="space-y-2">
          {uniqueErrors.map((err) => (
            <li key={err} className="text-sm text-rose-700 flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
              <span>{t('sf.fix_how', 'How to fix:')} {err}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-rose-600 mt-3">
          {t('sf.need_help', 'Need help?')}{' '}
          <Link to="/contact" className="font-semibold underline hover:text-rose-700">
            {t('form.contact_support', 'Contact support')}
          </Link>
        </p>
      </Card>
    )}
    <Card className="p-6 rounded-2xl border border-gray-100 bg-white">
      <h3 className="font-bold text-[#0f2540] mb-4">{t('sf.how_works', 'How it works')}</h3>
      <ol className="space-y-4">
        {[
          { id: 'pick', icon: Briefcase, title: t('sf.step_pick_title', 'Pick your services'), desc: t('sf.step_pick_desc', 'Select one or more services you need.') },
          { id: 'callback', icon: Phone, title: t('sf.step_cb_title', 'Get a callback'), desc: t('sf.step_cb_desc', 'A specialist contacts you within 2 hours.') },
          { id: 'deliver', icon: CheckCircle2, title: t('sf.step_deliver_title', 'We deliver'), desc: t('sf.step_deliver_desc', 'On-time delivery with full transparency.') },
        ].map((s, index) => (
          <li key={s.id} className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center shrink-0"><s.icon className="w-4 h-4 text-[#14A098]" /></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#14A098]">{t('common.step_label', 'Step {n}', { n: index + 1 })}</div>
              <div className="text-sm font-semibold text-[#0f2540]">{s.title}</div>
              <div className="text-xs text-gray-500">{s.desc}</div>
            </div>
          </li>
        ))}
      </ol>
    </Card>
    <Card className="p-6 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white">
      <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5" /><span className="font-semibold">{t('sf.secure_title', 'Secure & Confidential')}</span></div>
      <p className="text-sm text-teal-100 leading-relaxed">{t('sf.secure_body', 'All data is transmitted over HTTPS and accessed only by authorised CAs. We never share your business details.')}</p>
    </Card>
    {count > 0 && (
      <Card className="p-6 rounded-2xl border border-[#14A098]/30 bg-teal-50/40">
        <div className="text-xs uppercase tracking-wider text-[#14A098] font-semibold mb-1">{t('sf.applying_for', 'You\'re applying for')}</div>
        <div className="text-2xl font-extrabold text-[#0f2540]">{count > 1 ? t('sf.service_count_plural', '{n} services', { n: count }) : t('sf.service_count', '{n} service', { n: count })}</div>
        <div className="text-xs text-gray-500 mt-1">{t('sf.bundle_note', 'Bundle discounts may apply.')}</div>
      </Card>
    )}
  </div>
  );
};

const ServiceInfoHighlight = ({ serviceGuide, displayTitle, customStartingPrice }) => {
  const { t } = useLanguage();
  if (!serviceGuide) return null;
  return (
    <Card className="p-5 md:p-6 rounded-2xl border border-[#14A098]/30 bg-gradient-to-br from-teal-50/80 to-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <h3 className="ui-title-md">{displayTitle || serviceTitle(serviceGuide.code, t, serviceGuide.title)}</h3>
        <Badge className="bg-white border border-[#14A098]/25 text-[#0f2540]">
          {t(`category.${serviceGuide.category}`, serviceGuide.category)}
        </Badge>
      </div>
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 md:gap-5">
        <div className="rounded-2xl border-2 border-[#14A098]/35 bg-white p-4 md:p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-[#14A098] mb-2">{t('sf.starting_price', 'Starting price')}</p>
          <p className="text-2xl md:text-3xl font-extrabold text-[#0f2540] tracking-tight tabular-nums leading-tight">
            {customStartingPrice || <ServicePrice serviceCode={serviceGuide.code} />}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5 shadow-sm">
          <p className="text-base font-bold uppercase tracking-wide text-[#0f2540] leading-snug mb-2">{t('services.timeline', 'Expected timeline')}</p>
          <div className="text-sm text-[#0f2540] leading-relaxed">
            <GuideText>{categoryTimeline(serviceGuide.category, t, serviceGuide.timeline)}</GuideText>
          </div>
        </div>
      </div>
    </Card>
  );
};

const GSTRegistrationGuide = ({ entityType, onEntityTypeChange }) => {
  const { t } = useLanguage();
  const selectedEntity = GST_REGISTRATION_ENTITY_GUIDE.find((item) => item.entityType === entityType) || GST_REGISTRATION_ENTITY_GUIDE[0];

  return (
    <Card className="p-5 rounded-2xl border border-[#14A098]/30 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="ui-title-md">{t('sf.gst_guide_title', 'GST Registration Pricing & Documents')}</h3>
        <Badge className="bg-[#14A098]/10 text-[#0f2540] border border-[#14A098]/25">{t('sf.gst_guide_badge', 'Business type wise')}</Badge>
      </div>
      <p className="ui-note mb-4">{t('sf.gst_guide_note', 'Choose your business type to view exact starting price and required documents.')}</p>

      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        {GST_REGISTRATION_ENTITY_GUIDE.map((item) => (
          <button
            key={item.entityType}
            type="button"
            onClick={() => onEntityTypeChange(item.entityType)}
            className={`rounded-xl border px-3 py-2 text-sm font-semibold text-left transition-colors ${
              entityType === item.entityType
                ? 'border-[#14A098] bg-teal-50 text-[#0f2540]'
                : 'border-gray-200 bg-white text-[#0f2540] hover:border-[#14A098]/40'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        <h4 className="font-semibold text-[#0f2540] mb-3">{t('sf.gst_docs_title', 'Required documents ({label})', { label: selectedEntity.label })}</h4>
        <ul className="space-y-2">
          {selectedEntity.docs.map((doc) => (
            <li key={doc} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle2 className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
              <span>{doc}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

const SuccessView = ({ payload, onAbout }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const timer = setTimeout(() => onAbout(), 3000);
    return () => clearTimeout(timer);
  }, [onAbout]);

  const firstName = payload.full_name.split(' ')[0];
  const n = payload.service_required.length;

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Card className="p-8 md:p-12 rounded-2xl border border-gray-100 shadow-xl bg-white text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-lg mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">{t('sf.success_thanks', 'Thank you, {name}!', { name: firstName })}</h1>
          <p className="text-gray-600 mb-6">{t('sf.success_body', 'We\'ve received your request for {n} service(s). Our expert will reach you at +91 {phone} within 2 hours.', { n, phone: payload.mobile })}</p>
          <p className="text-sm text-gray-500">{t('sf.success_redirect', 'Redirecting to About page...')}</p>
        </Card>
      </div>
    </div>
  );
};

const ServicesForm = ({ embedded = false, useHomeLayout = false }) => {
  const { t, locale } = useLanguage();
  const homeLayout = embedded || useHomeLayout;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preCode = params.get('service');

  // isIncomeTaxService is an imported pure function — stable reference
  useEffect(() => {
    if (preCode && isIncomeTaxService(preCode)) {
      navigate(`/file-itr?service=${preCode}`, { replace: true });
    }
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preCode, navigate]);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'SERVICES_MULTI_FORM',
      form_name: 'ServicesForm',
      service_code: preCode || null,
      open_event_name: 'services_form_open',
    }),
    [preCode],
  );

  const [form, setForm] = useState(() => ({
    ...initialState,
    language: defaultSpokenLanguageForUiLocale(locale),
    service_required: preCode && !isIncomeTaxService(preCode) ? [preCode] : [],
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [gstEntityType, setGstEntityType] = useState('PROPRIETARY');
  const singleServiceMode = Boolean(preCode) && !embedded;
  const selectedServiceMeta = preCode ? SERVICE_CATALOG.find((s) => s.code === preCode) : null;
  const selectedServiceGuide = preCode ? getServiceGuide(preCode) : null;
  const fieldRefs = useRef({});
  const topCategoryButtons = useMemo(
    () => NAV_CATEGORIES.map((cat) => ({
      category: cat.category,
      label: cat.category === 'INCOME_TAX'
        ? t('sf.top_cat_itr', 'ITR')
        : cat.category === 'GST'
          ? t('sf.top_cat_gst', 'GST & Filing')
          : t(`category.${cat.category}`, cat.label),
      href: cat.category === 'INCOME_TAX'
        ? '/file-itr?service=ITR_FILING'
        : `/services/${cat.category.toLowerCase()}`,
      tone: cat.category === 'INCOME_TAX'
        ? 'bg-teal-100 border-teal-300 text-teal-900 hover:bg-teal-200/80'
        : cat.category === 'GST'
          ? 'bg-sky-100 border-sky-300 text-sky-900 hover:bg-sky-200/80'
          : cat.category === 'COMPANY'
            ? 'bg-violet-100 border-violet-300 text-violet-900 hover:bg-violet-200/80'
            : cat.category === 'MCA'
              ? 'bg-indigo-100 border-indigo-300 text-indigo-900 hover:bg-indigo-200/80'
              : cat.category === 'ACCOUNTING'
                ? 'bg-emerald-100 border-emerald-300 text-emerald-900 hover:bg-emerald-200/80'
                : cat.category === 'PAYROLL'
                  ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200/80'
                  : cat.category === 'TRADEMARK'
                    ? 'bg-rose-100 border-rose-300 text-rose-900 hover:bg-rose-200/80'
                    : cat.category === 'LICENSE'
                      ? 'bg-slate-100 border-slate-300 text-slate-900 hover:bg-slate-200/80'
                      : 'bg-gray-100 border-gray-300 text-gray-900 hover:bg-gray-200/80',
    })),
    [t],
  );

  useEffect(() => {
    const def = defaultSpokenLanguageForUiLocale(locale);
    if (!def) return;
    setForm((prev) => (prev.language ? prev : { ...prev, language: def }));
  }, [locale]);

  const selectedServices = useMemo(
    () => SERVICE_CATALOG.filter((svc) => form.service_required.includes(svc.code)),
    [form.service_required],
  );

  const selectionTotal = useMemo(() => {
    let sum = 0;
    let parsedCount = 0;
    let hasMonthly = false;
    selectedServices.forEach((svc) => {
      const raw = getServiceGuide(svc.code)?.startingPrice;
      const { amount, isMonthly } = parseInrStartingAmount(raw);
      if (amount != null) {
        sum += amount;
        parsedCount += 1;
      }
      if (isMonthly) hasMonthly = true;
    });
    return {
      sum,
      parsedCount,
      totalCount: selectedServices.length,
      hasMonthly,
      complete: parsedCount === selectedServices.length && selectedServices.length > 0,
    };
  }, [selectedServices]);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (submitError) setSubmitError('');
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const toggleService = (code) => {
    if (singleServiceMode) return;
    const wasSelected = form.service_required.includes(code);
    setForm((prev) => ({
      ...prev,
      service_required: prev.service_required.includes(code)
        ? prev.service_required.filter((c) => c !== code)
        : [...prev.service_required, code],
    }));
    trackEvent({
      event_name: wasSelected ? 'service_unselected' : 'service_selected',
      event_action: wasSelected ? 'remove' : 'select',
      event_status: 'success',
      event_category: 'form',
      form_name: 'ServicesForm',
      funnel_name: 'SERVICES_MULTI_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'service_selection',
      journey_id: getJourneyId('SERVICES_MULTI_FORM'),
      service_code: code,
      event_label: code,
    });
    if (errors.service_required) setErrors((prev) => ({ ...prev, service_required: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm(form, t);
    setErrors(errs);
    setSubmitError('');
    if (Object.keys(errs).length > 0) {
      trackEvent({
        event_name: 'services_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ServicesForm',
        funnel_name: 'SERVICES_MULTI_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('SERVICES_MULTI_FORM'),
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      toast.error(t('form.err_fix_toast', 'Please fix the highlighted errors'));
      const keyOrder = ['full_name', 'mobile', 'email', 'service_required'];
      const firstErrorKey = keyOrder.find((key) => errs[key]) || Object.keys(errs)[0];
      if (firstErrorKey === 'service_required') {
        const serviceInput = document.getElementById('services-search-input');
        if (serviceInput) serviceInput.focus();
      } else if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].focus();
      }
      return;
    }
    trackEvent({
      event_name: 'services_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ServicesForm',
      funnel_name: 'SERVICES_MULTI_FORM',
      funnel_step_number: 3,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('SERVICES_MULTI_FORM'),
      phone_number: form.mobile,
      event_label: form.service_required.join(','),
    });
    setSubmitting(true);
    const payload = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
      state: form.state || null,
      language: form.language || null,
      remark: form.remark.trim() || null,
      service_required: form.service_required,
      tag: 'client_website',
    };
    const endpoint = `${API_BASE}/api/v1/customers`;
    try {
      if (API_BASE) {
        // eslint-disable-next-line no-console
        console.info(`[SolveTax] POST ${endpoint}`, payload);
        await axios.post(endpoint, payload, {
          timeout: API_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
          },
        });
        trackEvent({
          event_name: 'services_submit_success',
          event_action: 'submit',
          event_status: 'success',
          event_category: 'form',
          form_name: 'ServicesForm',
          funnel_name: 'SERVICES_MULTI_FORM',
          funnel_step_number: 3,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('SERVICES_MULTI_FORM', { reset: true }),
          phone_number: form.mobile,
          event_label: form.service_required.join(','),
          api_name: '/api/v1/customers',
          api_status_code: 201,
        });
        toast.success(t('form.toast_submit_ok', 'Submitted to your backend successfully.'));
      } else {
        await new Promise((r) => setTimeout(r, MOCK_DELAY_MS));
        toast.success(t('form.toast_mock_ok', 'Your request has been captured (mock).'));
      }
      setSuccess(payload);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SolveTax] submission failed', err);
      const { status, message, fields } = parseApiError(err);
      trackEvent({
        event_name: 'services_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ServicesForm',
        funnel_name: 'SERVICES_MULTI_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('SERVICES_MULTI_FORM'),
        phone_number: form.mobile,
        event_label: form.service_required.join(','),
        api_name: '/api/v1/customers',
        api_status_code: status || null,
        error_code: status ? `api_${status}` : 'api_error',
        error_message: message,
      });
      if (Object.keys(fields).length > 0) {
        setErrors((prev) => ({ ...prev, ...fields }));
      }
      const hasAlreadyExistsError = (
        typeof message === 'string' && message.toLowerCase().includes('already exists')
      ) || Object.values(fields).some(
        (value) => typeof value === 'string' && value.toLowerCase().includes('already exists'),
      );
      if (hasAlreadyExistsError) {
        setSubmitError(t('sf.record_exists', 'A record already exists with the provided details.'));
      }
      if (status === 401) toast.error(t('contact.toast_unauthorized', 'Unauthorized: missing or invalid public API key.'));
      else if (status === 429) toast.error(t('contact.toast_rate', 'Rate limit exceeded. Please try again in a moment.'));
      else if (status === 422) toast.error(t('contact.toast_validation', 'Validation: {msg}', { msg: message }));
      else if (status === 400 || status === 409) toast.error(t('contact.toast_validation', 'Validation: {msg}', { msg: message }));
      else toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg: message }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <SuccessView payload={success} onAbout={() => navigate('/about')} />;

  const headlineTitle = preCode
    ? serviceTitle(preCode, t, SERVICE_CATALOG.find((s) => s.code === preCode)?.title || preCode)
    : t('sf.multi_head', 'You can request multiple services');
  const submitCtaLabel = homeLayout && !singleServiceMode
    ? t('sf.get_services', 'Get services')
    : t('form.submit_details', 'Submit Details');

  return (
    <div className={embedded ? 'py-8 bg-white' : 'pt-28 pb-16 hero-gradient min-h-screen'}>
      <div className={`${homeLayout ? 'w-full max-w-[1680px]' : 'max-w-[1500px]'} mx-auto px-3 md:px-6`}>
        {embedded && !singleServiceMode && (
          <div className="mb-6 md:mb-8 w-full min-w-0">
            <ServiceMarquee linkable />
          </div>
        )}

        <div className={`text-center ${embedded ? 'mb-6' : 'mb-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
            <ListChecks className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('sf.multi_badge', 'Multi-service planner • Pick one or more services')}</span>
          </div>
          <h1 className={`${homeLayout ? 'ui-title-lg' : 'ui-title-xl'} mb-5`}>{headlineTitle}</h1>
          {!embedded && (
            <div className="mb-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 px-0.5">
                {topCategoryButtons.map((item) => (
                  <Link
                    key={item.category}
                    to={item.href}
                    onClick={() =>
                      trackEvent({
                        event_name: 'cta_click',
                        event_action: 'click',
                        event_status: 'success',
                        event_category: 'navigation',
                        cta_name: `services_top_${item.category.toLowerCase()}`,
                        page_path: '/get-started',
                      })
                    }
                    className={`inline-flex items-center justify-center rounded-full border ${item.tone} px-2.5 sm:px-3.5 py-1.5 text-[11px] sm:text-sm font-semibold text-center leading-snug min-h-9 shadow-sm hover:shadow-md hover:-translate-y-[1px] active:translate-y-0 transition-all`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {!homeLayout && (
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-4 mb-5 text-sm">
              <div className="rounded-xl bg-gradient-to-r from-[#14A098] to-[#0e7a74] border border-[#14A098] shadow-md p-3 text-white">
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{t('sf.step1', 'Step 1')}</div>
                <div className="flex items-start gap-2 justify-center md:justify-start"><FileCheck2 className="w-4 h-4 text-white shrink-0 mt-0.5" /><span className="font-semibold leading-snug text-left">{t('sf.s1', 'Fill the form, select required services')}</span><span className="ml-auto hidden md:inline font-bold shrink-0">-&gt;</span></div>
              </div>
              <div className="rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 border border-cyan-500 shadow-md p-3 text-white">
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{t('sf.step2', 'Step 2')}</div>
                <div className="flex items-start gap-2 justify-center md:justify-start"><PhoneCall className="w-4 h-4 text-white shrink-0 mt-0.5" /><span className="font-semibold leading-snug text-left">{t('sf.s2', 'Expert callback in 2 hours from our team')}</span><span className="ml-auto hidden md:inline font-bold shrink-0">-&gt;</span></div>
              </div>
              <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 border border-emerald-500 shadow-md p-3 text-white">
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{t('sf.step3', 'Step 3')}</div>
                <div className="flex items-start gap-2 justify-center md:justify-start"><ShieldCheck className="w-4 h-4 text-white shrink-0 mt-0.5" /><span className="font-semibold leading-snug text-left">{t('sf.s3', 'We will provide the required services')}</span></div>
              </div>
            </div>
          )}
        </div>

        <div className={`grid ${homeLayout ? 'lg:grid-cols-1' : 'lg:grid-cols-12'} gap-6`}>
          {!homeLayout && (
            <div className="lg:col-span-4 lg:order-1">
              <Sidebar count={form.service_required.length} errors={errors} submitError={submitError} />
            </div>
          )}
          <Card className={`${homeLayout ? '' : 'lg:col-span-8 lg:order-2'} p-6 md:p-10 rounded-2xl border border-gray-100 shadow-xl bg-white`}>
            <form onSubmit={handleSubmit} className="space-y-8">
              {submitError && (
                <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <p className="font-medium">{submitError}</p>
                  <p className="mt-1">
                    {t('form.need_help_case', 'Need help with this case?')}{' '}
                    <Link to="/contact" className="font-semibold underline hover:text-rose-800">
                      {t('form.contact_support', 'Contact support')}
                    </Link>
                  </p>
                </div>
              )}
              {singleServiceMode && (
                <>
                  <div className="w-full flex justify-center">
                    <div className="inline-flex items-center justify-center rounded-full border-2 border-[#14A098] bg-[#14A098] px-6 py-2.5 text-sm sm:text-base font-extrabold text-white text-center shadow-md shadow-[#14A098]/35 whitespace-normal leading-snug">
                      {t('sf.fill_below_form', 'Fill the below form and Submit')}
                    </div>
                  </div>
                  <FormSection title={t('sf.selected_service', 'Selected Service')} icon={Briefcase} hint={t('sf.preselected', 'Pre-selected from service page')}>
                  <ServiceInfoHighlight
                    serviceGuide={selectedServiceGuide}
                    displayTitle={serviceTitle(preCode, t, selectedServiceMeta?.title || preCode)}
                    customStartingPrice={
                      preCode === 'GST_REGISTRATION'
                        ? (GST_REGISTRATION_ENTITY_GUIDE.find((item) => item.entityType === gstEntityType)?.price || GST_REGISTRATION_ENTITY_GUIDE[0].price)
                        : null
                    }
                  />
                  {preCode === 'GST_REGISTRATION' && (
                    <div className="mt-4">
                      <GSTRegistrationGuide
                        entityType={gstEntityType}
                        onEntityTypeChange={setGstEntityType}
                      />
                    </div>
                  )}
                  </FormSection>
                </>
              )}

              <div className={`grid ${singleServiceMode ? 'grid-cols-1' : 'xl:grid-cols-2'} gap-8 items-start`}>
                <div className="space-y-8">
                  <FormSection title={t('sf.personal', 'Personal Details')} icon={User}>
                    <div className="grid md:grid-cols-2 gap-5">
                      <FieldWrap htmlFor="sf-full_name" label={t('sf.full_name', 'Full Name')} icon={User} required error={errors.full_name}>
                        <Input id="sf-full_name" name="full_name" autoComplete="name" ref={(el) => { fieldRefs.current.full_name = el; }} value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} placeholder={t('sf.placeholder_name', 'e.g. Anita Verma')} maxLength={150} />
                      </FieldWrap>
                      <FieldWrap htmlFor="sf-mobile" label={t('sf.mobile', 'Mobile Number')} icon={Phone} required error={errors.mobile}>
                        <Input id="sf-mobile" name="mobile" autoComplete="tel" ref={(el) => { fieldRefs.current.mobile = el; }} value={form.mobile} onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder={t('itr.placeholder_mobile', '10-digit mobile')} inputMode="numeric" />
                      </FieldWrap>
                      <FieldWrap htmlFor="sf-email" label={t('sf.email', 'Email Address')} icon={Mail} required error={errors.email}>
                        <Input id="sf-email" name="email" autoComplete="email" ref={(el) => { fieldRefs.current.email = el; }} type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder={t('sf.placeholder_email', 'your@email.com')} />
                      </FieldWrap>
                    </div>
                    <div className="pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowMoreDetails((prev) => !prev)} className="rounded-full border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white">
                        {showMoreDetails ? t('sf.hide_more', 'Hide additional details') : t('sf.add_more', 'Add more details (optional)')}
                      </Button>
                    </div>
                    {showMoreDetails && (
                      <div className="grid md:grid-cols-2 gap-5 pt-4">
                        <FieldWrap htmlFor="sf-language" label={t('sf.pref_lang', 'Preferred Language')} icon={Globe}>
                          <Select value={form.language} onValueChange={(v) => setField('language', v)}>
                            <SelectTrigger id="sf-language" name="language" aria-label={t('sf.pref_lang', 'Preferred Language')}><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
                            <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        </FieldWrap>
                        <FieldWrap htmlFor="sf-state" label={t('sf.state', 'State')} icon={MapPin}>
                          <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                            <SelectTrigger id="sf-state" name="state" aria-label={t('sf.state', 'State')}><SelectValue placeholder={t('itr.placeholder_state', 'Select state')} /></SelectTrigger>
                            <SelectContent className="max-h-72">{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </FieldWrap>
                      </div>
                    )}
                  </FormSection>

                  {showMoreDetails && (
                    <FormSection title={t('sf.remark_section', 'Additional Remarks')} icon={MessageSquare}>
                      <Textarea id="sf-remark" name="remark" value={form.remark} onChange={(e) => setField('remark', e.target.value)} rows={3} placeholder={t('sf.remark_ph', 'Any specific requirement or timeline (optional)')} />
                    </FormSection>
                  )}
                </div>

                {!singleServiceMode && (
                  <div className="xl:sticky xl:top-24">
                    <FormSection title={t('sf.services_req', 'Services Required')} icon={Briefcase} hint={t('sf.select_multi', 'Select multiple if needed')}>
                      <ServicePicker selected={form.service_required} onToggle={toggleService} error={errors.service_required} includeIncomeTax={homeLayout} />
                    </FormSection>
                  </div>
                )}
              </div>

              {!singleServiceMode && selectedServices.length > 0 && (
                <div className="rounded-2xl border-2 border-[#14A098]/35 bg-gradient-to-br from-teal-50/90 to-white p-4 md:p-5 shadow-sm">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#14A098] mb-2">
                    {t('sf.review_before_submit', 'Review before submit')}
                  </p>
                  <p className="text-sm font-semibold text-[#0f2540] mb-3">
                    {selectedServices.length > 1
                      ? t('sf.service_count_plural', '{n} services selected', { n: selectedServices.length })
                      : t('sf.service_count', '{n} service selected', { n: selectedServices.length })}
                  </p>
                  <div className="mb-3 grid gap-2 sm:grid-cols-2">
                    {selectedServices.map((svc) => {
                      return (
                        <div key={svc.code} className="rounded-lg border border-[#14A098]/25 bg-white px-3 py-2">
                          <p className="text-xs font-semibold text-[#0f2540] leading-snug">
                            {serviceTitle(svc.code, t, svc.title)}
                          </p>
                          <p className="text-[11px] font-bold text-[#14A098] mt-1">
                            <ServicePrice serviceCode={svc.code} />
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {selectionTotal.sum <= 0 && (
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-[#0f2540]">{t('sf.total_label_short', 'Total amount:')}</span>{' '}
                      {t('sf.total_unknown', 'starting prices not available for the current selection — our team will confirm on call.')}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2 items-center">
                <Button type="submit" size="lg" disabled={submitting} className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 min-w-[200px] shadow-lg shadow-[#14A098]/30 whitespace-normal text-center leading-snug">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('itr.submitting', 'Submitting...')}</>) : (<>{submitCtaLabel} <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm({
                    ...initialState,
                    language: defaultSpokenLanguageForUiLocale(locale),
                    service_required: preCode && !isIncomeTaxService(preCode) ? [preCode] : [],
                  })}
                  className="rounded-full h-12 border-gray-300"
                >
                  {t('itr.reset', 'Reset')}
                </Button>
                <p className="text-xs text-gray-500 mt-2 w-full">By submitting, you agree to our <span className="text-[#14A098] underline cursor-pointer">Terms</span> &amp; <span className="text-[#14A098] underline cursor-pointer">Privacy Policy</span>.</p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServicesForm;
